import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { authApi, analyticsApi } from '../../services/api';
import { Card, CardContent } from '../ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/AlertDialog';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

const ProfilePage = () => {
  const { currentUser, isAnonymous, isAdmin, deleteAccount, reauthenticate, logout } = useAuth();
  const { credits } = useCredits();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [usageStats, setUsageStats] = useState({
    totalTextsHumanized: 0,
    totalCreditsSpent: 0,
    creditsRemaining: 0,
  });
  const [usageHistory, setUsageHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState(null);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      // For anonymous users, just set loading to false and return
      if (!currentUser || isAnonymous) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const userResponse = await authApi.getCurrentUser();
        
        if (userResponse.success) {
          setDisplayName(userResponse.data.displayName);
          setEmail(userResponse.data.email);
        }

        // Fetch analytics data
        try {
          const analyticsResponse = await analyticsApi.getStats();
          if (analyticsResponse.success) {
            setUsageStats({
              totalTextsHumanized: analyticsResponse.data.textsHumanized,
              totalCreditsSpent: analyticsResponse.data.totalCreditsSpent,
              creditsRemaining: analyticsResponse.data.creditsRemaining,
            });
          }
          
          // Fetch usage history
          const historyResponse = await analyticsApi.getHistory();
          if (historyResponse.success) {
            setUsageHistory(historyResponse.data.usageHistory);
          }
        } catch (analyticsError) {
          console.error('Error fetching analytics:', analyticsError);
          // Don't block the whole profile if analytics fail
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, isAnonymous]);

  const handleDeleteAccount = async () => {
    try {
      setDeleteError(null);
      
      // Pass the password for re-authentication
      await deleteAccount(deletePassword);
      // The user will be redirected or signed in anonymously by the AuthContext
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.message || 'Failed to delete account. Please verify your password and try again.');
    }
  };

  const handlePasswordChange = async () => {
    // Reset states
    setPasswordChangeError(null);
    setPasswordChangeSuccess(false);
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setPasswordChangeError("New passwords don't match");
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordChangeError("Password must be at least 6 characters");
      return;
    }
    
    try {
      // First re-authenticate the user
      await reauthenticate(currentPassword);
      
      // Then update the password
      await currentUser.updatePassword(newPassword);
      
      // Clear form and show success message
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordChangeSuccess(true);
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordChangeError(err.message || 'Failed to change password. Please verify your current password.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  // Format timestamp to human-readable
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  // Show login/signup prompt for anonymous users
  if (isAnonymous) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8 text-foreground">My Account</h1>
        
        <div className="flex justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <Avatar className="h-20 w-20 mx-auto mb-4">
                  <AvatarFallback className="bg-muted text-muted-foreground text-2xl">
                    ?
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Welcome, Guest!</h2>
                <p className="text-muted-foreground">
                  Create an account or log in to access your profile, view your usage history, and manage your credits.
                </p>
              </div>
              
              <div className="space-y-3">
                <Link to="/register" className="block">
                  <Button className="w-full bg-primary hover:bg-primary/90">
                    Create Account
                  </Button>
                </Link>
                
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">
                    Log In
                  </Button>
                </Link>
              </div>
              
              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> As a guest user, you have limited access to features. 
                  Creating an account unlocks full functionality and saves your progress.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8 text-foreground">My Account</h1>
      
      <div className="grid gap-6 md:grid-cols-[300px_1fr]">
        {/* Profile Card */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-primary/10 p-6 flex flex-col items-center">
              <Avatar className="h-24 w-24 mb-4">
                <AvatarImage src="/placeholder.svg" />
                <AvatarFallback className="bg-primary/20 text-primary text-xl">
                  {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-semibold text-foreground">{displayName}</h2>
              <p className="text-muted-foreground">{email}</p>
              {isAdmin && (
                <Badge className="mt-2 bg-primary/20 text-primary hover:bg-primary/30">Admin</Badge>
              )}
            </div>
            <div className="p-6 bg-card h-full">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Credits Balance</p>
                  <p className="text-2xl font-bold text-primary">{credits?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Status</p>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                </div>
                {isAdmin && (
                  <div className="pt-2">
                    <Link to="/admin">
                      <Button className="w-full bg-primary hover:bg-primary/90">
                        Go to Admin Dashboard
                      </Button>
                    </Link>
                  </div>
                )}
                
                {!isAnonymous && (
                  <div className="pt-4 border-t border-border mt-4">
                    <p className="text-sm text-muted-foreground mb-2">Account Management</p>
                    
                    {/* Logout Button */}
                    <Button 
                      onClick={handleLogout}
                      variant="outline" 
                      className="w-full mb-2"
                    >
                      Logout
                    </Button>
                    
                    {/* Change Password Button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="w-full mb-2"
                        >
                          Change Password
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Change Your Password</AlertDialogTitle>
                          <AlertDialogDescription>
                            Enter your current password and a new password below.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        
                        {passwordChangeError && (
                          <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded mb-4">
                            {passwordChangeError}
                          </div>
                        )}
                        
                        {passwordChangeSuccess && (
                          <div className="bg-green-100 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                            Password changed successfully!
                          </div>
                        )}
                        
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="current-password">Current Password</Label>
                            <Input 
                              id="current-password" 
                              type="password" 
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="new-password">New Password</Label>
                            <Input 
                              id="new-password" 
                              type="password" 
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="confirm-password">Confirm New Password</Label>
                            <Input 
                              id="confirm-password" 
                              type="password" 
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handlePasswordChange}
                            className="bg-primary hover:bg-primary/90"
                          >
                            Change Password
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    
                    {/* Delete Account Button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your
                            account and remove all your data from our servers.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        
                        {deleteError && (
                          <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded mb-4">
                            {deleteError}
                          </div>
                        )}
                        
                        <div className="mb-4">
                          <Label htmlFor="delete-password">Enter your password to confirm</Label>
                          <Input 
                            id="delete-password" 
                            type="password" 
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleDeleteAccount}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete Account
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard & Analytics */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-foreground">Dashboard & Analytics</h2>
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="usage">Usage History</TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-card p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Total Texts Enhanced</p>
                      <p className="text-2xl font-bold text-primary">{usageStats.totalTextsHumanized}</p>
                    </div>
                    <div className="bg-card p-4 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">Total Credits Spent</p>
                      <p className="text-2xl font-bold text-primary">{usageStats.totalCreditsSpent}</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="usage">
                  <div className="border rounded-lg border-border">
                    <div className="p-4 border-b bg-muted/50 border-border">
                      <p className="text-sm text-muted-foreground">Recent Activity</p>
                    </div>
                    <div className="p-4 bg-card">
                                              {usageHistory.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-border">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Mode</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Credits Used</th>
                                </tr>
                              </thead>
                              <tbody className="bg-card divide-y divide-border">
                                {usageHistory.map((item, index) => (
                                  <tr key={index} className="hover:bg-muted/50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(item.timestamp)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{item.operationType}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{item.mode}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{item.creditsUsed}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-center py-8">
                            No usage history found. Start enhancing text to see your activity here!
                          </p>
                        )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 